import styles from './SplashScreen.module.css';

/**
 * Full-screen loader shown while a document is authenticated, from the "Pad"
 * design artboard: a badge, a stack of pages where the front one writes itself
 * (heading bar, body lines, two collaborator cursors, a caret), then the
 * wordmark with a pulsing ellipsis. `exiting` plays the fade-out; the parent
 * unmounts it once that finishes. Same overlay and exit as milegrid's splash so
 * the two apps feel the same.
 */

// Body text of the front page. Each entry is a skeleton line: where it sits,
// how wide it is, and when it grows in. Highlights are the two "selections"
// the collaborators leave behind.
const LINES = [
  { left: 18, top: 42, width: 170, delay: '.95s' },
  { left: 18, top: 56, width: 150, delay: '1.07s' },
  { left: 18, top: 70, width: 60, delay: '1.19s', highlight: styles.highlightBlue },
  { left: 82, top: 70, width: 100, delay: '1.25s' },
  { left: 18, top: 102, width: 150, delay: '1.6s' },
  { left: 18, top: 116, width: 60, delay: '1.72s', highlight: styles.highlightRed },
  { left: 84, top: 116, width: 90, delay: '1.78s' },
  { left: 18, top: 130, width: 110, delay: '1.9s' },
];

const CursorGlyph = ({ fill }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill={fill} aria-hidden="true">
    <path d="M4 3l16 8-7 2-2 7z" />
  </svg>
);

const SplashScreen = ({ className = '', exiting = false }) => (
  <div
    className={[styles.overlay, exiting && styles.exiting, className].filter(Boolean).join(' ')}
    role="status"
    aria-busy="true"
    aria-live="polite"
    aria-label="Loading document"
  >
    <div className={styles.stack}>
      <div className={styles.badge} aria-hidden="true">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="1.8">
          <path d="M6 3h9l5 5v13H6z" />
          <path d="M15 3v5h5" />
          <path d="M9 13h6M9 16h6M9 10h3" />
        </svg>
      </div>

      <div className={styles.canvas} aria-hidden="true">
        <div className={styles.glow} />

        <div className={`${styles.page} ${styles.pageBack}`} />
        <div className={`${styles.page} ${styles.pageMiddle}`} />

        <div className={`${styles.page} ${styles.pageFront}`}>
          <div className={styles.rail}>
            <svg className={styles.sparkle} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#526bb1" strokeWidth="1.8">
              <path d="M12 3l1.8 4.6L18 9l-4.2 1.4L12 15l-1.8-4.6L6 9l4.2-1.4z" />
            </svg>
            <span className={`${styles.chip} ${styles.chipActive}`} style={{ animationDelay: '1s' }} />
            <span className={styles.chip} style={{ animationDelay: '1.15s' }} />
            <span className={styles.chip} style={{ animationDelay: '1.3s' }} />
          </div>

          <div className={styles.body}>
            <div className={styles.heading} />

            {LINES.map(({ left, top, width, delay, highlight }) => (
              <div
                key={`${left}-${top}`}
                className={`${styles.line} ${highlight || ''}`}
                style={{ left, top, width, animationDelay: delay }}
              />
            ))}

            <div className={`${styles.cursor} ${styles.cursorBlue}`}>
              <CursorGlyph fill="#5b8dd6" />
            </div>
            <div className={`${styles.cursor} ${styles.cursorRed}`}>
              <CursorGlyph fill="#e2716b" />
            </div>
            <div className={styles.collaborator}>JR</div>

            <div className={styles.caret} />
          </div>
        </div>
      </div>

      <div className={styles.wordmark}>
        <div className={styles.title}>Pad</div>
        <div className={styles.dots}>
          <span className={styles.dot} style={{ animationDelay: '0s' }} />
          <span className={styles.dot} style={{ animationDelay: '.15s' }} />
          <span className={styles.dot} style={{ animationDelay: '.3s' }} />
        </div>
      </div>
    </div>
  </div>
);

export default SplashScreen;
